/**
 * KAN-840: время в часовом поясе организации (Organization.system_timezone,
 * приходит как organization_timezone в /auth/me и ответе входа).
 *
 * Поле datetime-local ("ГГГГ-ММ-ДДTЧЧ:ММ") вводится по времени организации и
 * переводится в ISO (UTC); ISO показывается по времени организации. Только Intl,
 * без сторонних библиотек. Неизвестный или пустой пояс: Москва, у московских
 * организаций ничего не меняется.
 */

export const DEFAULT_ORG_TIMEZONE = 'Europe/Moscow';

export type ZonedParts = Record<'year' | 'month' | 'day' | 'hour' | 'minute', number>;

const INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * MINUTE_MS;

const formatters = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}

/** IANA-имя, если браузер его знает; пусто или неизвестно: Москва. */
export function resolveTimezone(timeZone: string | null | undefined): string {
  const name = typeof timeZone === 'string' ? timeZone.trim() : '';
  if (!name) return DEFAULT_ORG_TIMEZONE;
  try {
    partsFormatter(name);
    return name;
  } catch {
    return DEFAULT_ORG_TIMEZONE;
  }
}

/** Части даты в поясе: год, месяц (1..12), день, час, минута. */
export function zonedParts(date: Date, timeZone: string = DEFAULT_ORG_TIMEZONE): ZonedParts {
  const parts = partsFormatter(resolveTimezone(timeZone)).formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute') };
}

function parseIso(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Смещение пояса от UTC в момент `ms`, в миллисекундах (Екатеринбург: +5 ч). */
function offsetAt(ms: number, timeZone: string): number {
  const p = zonedParts(new Date(ms), timeZone);
  const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  return wall - Math.floor(ms / MINUTE_MS) * MINUTE_MS;
}

/** ISO -> "ГГГГ-ММ-ДДTЧЧ:ММ" по времени пояса для datetime-local; пусто или мусор: "". */
export function isoToZonedInput(iso: string | null | undefined, timeZone: string = DEFAULT_ORG_TIMEZONE): string {
  const date = parseIso(iso);
  if (!date) return '';
  const p = zonedParts(date, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** "ГГГГ-ММ-ДДTЧЧ:ММ" по времени пояса -> ISO (UTC); пусто или мусор: null. */
export function zonedInputToIso(value: string, timeZone: string = DEFAULT_ORG_TIMEZONE): string | null {
  const match = INPUT_RE.exec(value);
  if (!match) return null;
  const [year, month, day, hour, minute] = match.slice(1).map(Number);
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  const check = new Date(wall);
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day ||
    check.getUTCHours() !== hour ||
    check.getUTCMinutes() !== minute
  ) {
    return null;
  }
  const zone = resolveTimezone(timeZone);
  // Смещения за сутки до и после: у поясов с летним временем они разные.
  // Неоднозначное время (перевод назад) даёт более ранний момент; время,
  // которого нет (перевод вперёд), переводится по смещению до перехода и
  // сдвигается вперёд (как Temporal "compatible").
  const before = offsetAt(wall - DAY_MS, zone);
  const after = offsetAt(wall + DAY_MS, zone);
  const matches = [wall - before, wall - after].filter((utc) => offsetAt(utc, zone) === wall - utc);
  const utc = matches.length ? Math.min(...matches) : wall - before;
  return new Date(utc).toISOString();
}

/** "12.10 14:00" по времени пояса; пусто или мусор: "". */
export function formatZonedShort(iso: string | null | undefined, timeZone: string = DEFAULT_ORG_TIMEZONE): string {
  const date = parseIso(iso);
  if (!date) return '';
  const p = zonedParts(date, timeZone);
  return `${pad(p.day)}.${pad(p.month)} ${pad(p.hour)}:${pad(p.minute)}`;
}

/** Ключ дня в поясе ГГГГММДД (сравнение "сегодня или раньше"). */
export function zonedDayKey(date: Date, timeZone: string = DEFAULT_ORG_TIMEZONE): number {
  const p = zonedParts(date, timeZone);
  return p.year * 10000 + p.month * 100 + p.day;
}

/** Русские названия частых поясов; остальные показываются IANA-именем. */
const CITY_NAMES: Readonly<Record<string, string>> = {
  'Europe/Moscow': 'Москва',
  'Europe/Kaliningrad': 'Калининград',
  'Europe/Samara': 'Самара',
  'Europe/Volgograd': 'Волгоград',
  'Europe/Saratov': 'Саратов',
  'Europe/Ulyanovsk': 'Ульяновск',
  'Europe/Astrakhan': 'Астрахань',
  'Europe/Kirov': 'Киров',
  'Europe/Minsk': 'Минск',
  'Asia/Yekaterinburg': 'Екатеринбург',
  'Asia/Omsk': 'Омск',
  'Asia/Novosibirsk': 'Новосибирск',
  'Asia/Barnaul': 'Барнаул',
  'Asia/Tomsk': 'Томск',
  'Asia/Novokuznetsk': 'Новокузнецк',
  'Asia/Krasnoyarsk': 'Красноярск',
  'Asia/Irkutsk': 'Иркутск',
  'Asia/Chita': 'Чита',
  'Asia/Yakutsk': 'Якутск',
  'Asia/Vladivostok': 'Владивосток',
  'Asia/Sakhalin': 'Южно-Сахалинск',
  'Asia/Magadan': 'Магадан',
  'Asia/Kamchatka': 'Петропавловск-Камчатский',
  'Asia/Anadyr': 'Анадырь',
  'Asia/Almaty': 'Алматы',
  'Asia/Tashkent': 'Ташкент',
  'Asia/Bishkek': 'Бишкек',
  'Asia/Dubai': 'Дубай',
  UTC: 'UTC',
};

/** Название пояса для подписи: "Екатеринбург"; неизвестный: IANA-имя. */
export function timezoneCityName(timeZone: string | null | undefined): string {
  const zone = resolveTimezone(timeZone);
  return CITY_NAMES[zone] ?? zone;
}

/** Подпись поля времени: "по времени организации (Екатеринбург)". */
export function orgTimezoneLabel(timeZone: string | null | undefined): string {
  return `по времени организации (${timezoneCityName(timeZone)})`;
}
